import React, { useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import ImagenNoDisponibleKawaii from "../ImagenNoDisponibleKawaii";

// Template56: Página de Kamakura de Tokio
// Copia DIRECTA del componente Page15.jsx de guia-tokio, adaptado para usar las props del sistema guia
const Template56 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [kamakuraImageError, setKamakuraImageError] = useState(false);
	const [kamakura2ImageError, setKamakura2ImageError] = useState(false);
	const [hasaderaImageError, setHasaderaImageError] = useState(false);
	const [kamakuraStickerError, setKamakuraStickerError] = useState(false);

	const logoImage =
		page.canvasImages?.find((img) => img.id === "logo")?.imageUrl;

	// Lógica especial para la guía de Tokio: header #24221a en primera página, #e24217 en las siguientes
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
			? page.data.order
			: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0
			? "#e24217"
			: headerBackgroundColor;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const kamakuraImage = getImageSrc("kamakura-main");
	const kamakura2Image = getImageSrc("kamakura-templo");
	const hasaderaImage = getImageSrc("hasedera");
	const kamakuraSticker = getCanvasImageUrl("kamakura-sticker");

	// Textos desde page.texts (índices 0-12, solo los que se usan en la UI)
	const headerText = page.texts?.[0]?.content;
	const heroTitle = page.texts?.[1]?.content;
	const introCorta = page.texts?.[2]?.content;
	const comoLlegarTitle = page.texts?.[3]?.content;
	const comoLlegarContent = page.texts?.[4]?.content;
	const budaTitle = page.texts?.[5]?.content;
	const budaCardText = page.texts?.[6]?.content;
	const hasederaTitle = page.texts?.[8]?.content;
	const hasederaText = page.texts?.[9]?.content;
	const tsurugaokaTitle = page.texts?.[11]?.content;
	const tsurugaokaText = page.texts?.[12]?.content;

	// Parsear Sabías que (emoji + texto) para los dos bloques
	const parseSabiasQueItem = (html) => {
		if (!html) return { icono: "📌", texto: "" };
		const m = html.match(/<p>([^\s]+)\s+(.*?)<\/p>/s);
		if (m) return { icono: m[1], texto: m[2].replace(/<[^>]*>/g, "").trim() };
		return { icono: "📌", texto: html.replace(/<[^>]*>/g, "").trim() };
	};
	const sabiasQueBuda = parseSabiasQueItem(page.texts?.[7]?.content);
	const sabiasQueHasedera = parseSabiasQueItem(page.texts?.[10]?.content);

	// Estilos reutilizables
	const estiloH3 = {
		fontFamily: `${fontFamily} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const estiloP = {
		fontFamily: `${fontFamily} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor,
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "10px !important",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor,
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{headerText}
				<Box
					component="img"
					src={logoImage}
					alt="logo"
					sx={{
						width: isSmallScreen ? 66 : 80,
						position: "absolute",
						right: 2,
						top: 18,
						zIndex: 999999,
					}}
				/>
			</Box>

			{/* IMAGEN PORTADA */}
			<Box data-block-id="image-0" sx={{ position: "relative" }}>
				{kamakuraImage && !kamakuraImageError ? (
					<img
						src={kamakuraImage}
						alt=""
						onError={() => setKamakuraImageError(true)}
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
				) : (
					<ImagenNoDisponibleKawaii width="100%" height={200} fontFamily={fontFamily} message="Imagen no disponible" compact />
				)}

				{kamakuraSticker && !kamakuraStickerError && (
					<img
						src={kamakuraSticker}
						alt=""
						onError={() => setKamakuraStickerError(true)}
						style={{
							width: isSmallScreen ? 80 : 100,
							position: "absolute",
							bottom: isSmallScreen ? 60 : -10,
							right: isSmallScreen ? "auto" : 0,
							left: isSmallScreen ? 25 : "auto",
						}}
					/>
				)}
				<Box
					component="h1"
					sx={{
						fontFamily: `${fontFamily} !important`,
						fontSize: 32,
						textAlign: "center",
						position: "absolute",
						bottom: 0,
						margin: "10px",
						left: "20px",
						color: "#fff",
						textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
					}}
				>
					{heroTitle}
				</Box>
			</Box>

			<Box
				data-block-id="text-1"
				sx={{
					padding: "0px 12px",
					marginBottom: "0px",
					paddingTop: "4px",
				}}
			>
				<Box component="p" sx={{ ...estiloP, marginBottom: "0px!important" }}>
					{introCorta}
				</Box>
			</Box>

			{/* Cómo llegar */}
			<Box data-block-id="text-2" sx={{ padding: "8px 12px", marginBottom: "4px", marginTop: "-8px" }}>
				<Box
					sx={{
						backgroundColor: "#ffe3cc",
						border: "3px solid #f28b35",
						borderRadius: "12px",
						padding: "8px 20px",
						fontSize: 12,
						fontFamily: `${fontFamily} !important`,
						color: "#333",
						width: "100%",
					}}
				>
					<Box
						component="h4"
						sx={{
							margin: 0,
							marginBottom: "8px",
							fontSize: 13,
							fontWeight: 700,
							display: "flex",
							alignItems: "center",
							gap: 6,
							color: "#f28b35",
							textTransform: "uppercase",
							fontFamily: `${fontFamily} !important`,
						}}
					>
						{comoLlegarTitle}
					</Box>
					<Box component="span" dangerouslySetInnerHTML={{ __html: comoLlegarContent }} />
				</Box>
			</Box>

			{/* CONTENIDO */}
			<Box>
				{/* Gran Buda */}
				<Box
					data-block-id="text-3"
					sx={{
						display: "flex",
						gap: "16px",
						padding: "0 12px",
						position: "relative",
					}}
				>
					{kamakura2Image && !kamakura2ImageError ? (
						<img
							src={kamakura2Image}
							alt=""
							onError={() => setKamakura2ImageError(true)}
							style={{
								width: "40%",
								height: 100,
								objectFit: "cover",
								borderRadius: "12px",
							}}
						/>
					) : (
						<ImagenNoDisponibleKawaii width="40%" height={100} fontFamily={fontFamily} message="Imagen no disponible" compact />
					)}
					<Box
						sx={{
							position: "absolute",
							top: 0,
							left: "35%",
							zIndex: 999,
						}}
					>
						<SabiasQue icono={sabiasQueBuda.icono} texto={sabiasQueBuda.texto} />
					</Box>
					<Box sx={{ width: "60%" }}>
						<Box component="h3" sx={estiloH3}>
							{budaTitle}
						</Box>
						<Box component="p" sx={estiloP}>
							{budaCardText}
						</Box>
					</Box>
				</Box>

				{/* Templo Hasedera */}
				<Box
					data-block-id="text-4"
					sx={{
						display: "flex",
						gap: "16px",
						padding: "0 12px",
						marginTop: "8px",
						position: "relative",
					}}
				>
					{hasaderaImage && !hasaderaImageError ? (
						<img
							src={hasaderaImage}
							alt=""
							onError={() => setHasaderaImageError(true)}
							style={{
								width: "40%",
								height: 100,
								objectFit: "cover",
								borderRadius: "12px",
							}}
						/>
					) : (
						<ImagenNoDisponibleKawaii width="40%" height={100} fontFamily={fontFamily} message="Imagen no disponible" compact />
					)}

					<Box
						sx={{
							position: "absolute",
							top: 0,
							left: "35%",
							zIndex: 999,
						}}
					>
						<SabiasQue icono={sabiasQueHasedera.icono} texto={sabiasQueHasedera.texto} />
					</Box>

					<Box sx={{ width: "60%", position: "relative" }}>
						<Box component="h3" sx={estiloH3}>
							{hasederaTitle}
						</Box>
						<Box component="p" sx={estiloP}>
							{hasederaText}
						</Box>
					</Box>
				</Box>

				{/* Santuario Tsurugaoka Hachimangu */}
				<Box data-block-id="text-5" sx={{ padding: "0px 12px", paddingTop: "0px" }}>
					<Box
						sx={{
							backgroundColor: "#ffe3cc",
							padding: "12px",
							paddingBottom: "0px",
							borderRadius: "12px",
							display: "flex",
							gap: "16px",
							alignItems: "center",
							position: "relative",
							marginBottom: isSmallScreen ? "20px" : 0,
						}}
					>
						<Box sx={{ width: "100%" }}>
							<Box component="h3" sx={estiloH3}>
								{tsurugaokaTitle}
							</Box>
							<Box component="p" sx={estiloP}>
								{tsurugaokaText}
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

Template56.thumbnail = "/images/template-1.png";

export default Template56;
